import { isAuthenticated } from "@/lib/is-authenticated";
import { useOrganizationTeacherGetOrganizationSettings } from "@/orval/organization-teacher";
import { createFileRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import { BookOpenIcon } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarRail,
  SidebarSeparator,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { SidebarOrganizationHeader } from "./-components/sidebar-organization-header";
import { useState } from "react";
import { useAuthGetImpersonationStatus } from "@/orval/auth";
import { cn } from "@/lib/utils";
import { parseOrganizationIdParams } from "@/lib/route-params";
import { SidebarViewSwitcher } from "./-components/sidebar-footer-actions";
import { ProductEmailInvitation } from "@/components/product-email-invitation";

export const Route = createFileRoute("/_app/organizations/$organizationId/teacher")({
  staticData: { sidebarLayout: true },
  params: {
    parse: parseOrganizationIdParams,
  },
  beforeLoad: (context) => {
    if (location.pathname === `/organizations/${context.params.organizationId}/teacher`) {
      throw redirect({
        to: "/organizations/$organizationId/teacher/classes",
        params: context.params,
      });
    }

    return isAuthenticated(context);
  },
  component: RouteComponent,
});

const navigationItems = [
  {
    to: "/organizations/$organizationId/teacher/classes",
    icon: BookOpenIcon,
    label: "Classes",
  },
];

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const { data: teacherSettings } = useOrganizationTeacherGetOrganizationSettings(organizationId);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { data: impersonationStatus } = useAuthGetImpersonationStatus();

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="flex-1 flex flex-col min-h-full min-w-0 w-full">
        <div className="flex flex-row flex-1 min-w-0">
          <Sidebar
            className={cn(
              impersonationStatus?.isImpersonating ? "md:top-28 md:pb-28" : "md:top-16 md:pb-16",
              `w-64 border-r-muted border-r flex-none bg-background z-10`,
            )}
            collapsible="icon"
          >
            <SidebarHeader className="bg-background group-data-[collapsible=icon]:p-1">
              <SidebarOrganizationHeader
                logoUrl={teacherSettings?.organizationLogoUrl}
                organizationName={teacherSettings?.organizationName}
                organizationId={organizationId}
              />
            </SidebarHeader>

            <SidebarContent className="bg-background">
              <SidebarGroup className="pt-0">
                <SidebarGroupContent>
                  <SidebarMenu>
                    {navigationItems.map((item) => (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton asChild tooltip={item.label}>
                          <Link
                            to={item.to}
                            activeProps={{ className: "bg-secondary text-secondary-foreground/100" }}
                            from={Route.fullPath}
                          >
                            <item.icon />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>

            <SidebarFooter className="bg-background">
              <SidebarSeparator className="mx-0" />
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarViewSwitcher
                    label="Switch view"
                    currentView="Teacher"
                    organizationId={organizationId}
                    from={Route.fullPath}
                  />
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarFooter>
            <SidebarRail />
          </Sidebar>

          <div className="flex flex-col flex-1 min-w-0 md:w-auto w-full">
            <ProductEmailInvitation />
            <Outlet />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}
